import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_AiPortraitGenerator = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/ai-portrait-generator',
			name: 'AI Portrait Generator — Create Portrait Headshots | Lift',
			description:
				'Turn your photos into AI portraits instantly. No watermark. Upload selfies, choose your style, and get studio-quality business portraits in minutes. Try now!',
			url: 'https://liftapp.ai/ai-portrait-generator',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/ai-portrait-generator#product' },
				{ '@id': 'https://liftapp.ai/ai-portrait-generator#howto' },
				{ '@id': 'https://liftapp.ai/ai-portrait-generator#benefits' },
				{ '@id': 'https://liftapp.ai/ai-portrait-generator#faq' },
				{ '@id': 'https://liftapp.ai/ai-portrait-generator#why-lift' },
				{ '@id': 'https://liftapp.ai/ai-portrait-generator#explore-tools' },
				{ '@id': 'https://liftapp.ai/ai-portrait-generator#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/ai-portrait-generator#product',
			name: 'AI Portrait Generator Online',
			description:
				'Create AI portraits in minutes with no photo shoot required. Get studio-quality business portraits from your selfies, no watermarks.',
			url: 'https://liftapp.ai/ai-portrait-generator',
			fileFormat: ['image/jpeg', 'image/png', 'image/webp'],
			memoryRequirements: 'Max file size: 20MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/ai-portrait-generator#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your photos',
					text: "Choose a photo from your device or take one using Lift's app",
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Let the AI do the work',
					text: 'Watch as Lift generates professional AI portraits automatically',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Customize adjustments',
					text: 'Fine-tune your image with easy-to-use editing tools',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download and use',
					text: 'Download your image or publish it straight to your social feeds',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-portrait-generator#benefits',
			name: 'Who can benefit from AI Portrait Generator?',
			description:
				"Lift's AI Portrait Generator is useful for professionals, job seekers, creators, and businesses who want professional AI portraits and headshots for LinkedIn, portfolios, corporate websites, and personal branding.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Job Seekers & Career Switchers — Upgrade your LinkedIn profile and resume with professional AI headshots that boost credibility and make strong first impressions. Get interview-ready portraits without expensive photo shoots.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Freelancers & Solopreneurs — Build your personal brand with consistent, high-quality AI portraits for websites, social profiles, and pitch decks. Generate multiple professional styles in one session at a fraction of photographer costs.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Remote Teams — Unify your distributed workforce with consistent, professional headshots for company websites and team directories. No scheduling hassles, no studio logistics — just scalable, uniform portraits for every employee.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Personal Brand Builders — Refresh your online presence with eye-catching AI portraits that blend creativity with professionalism. Perfect for content creators, coaches, and influencers who need standout visuals across all platforms.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Students & educators — Create polished professional portraits for academic profiles, research publications, and career portfolios. Get affordable, high-quality headshots for conference bios, faculty pages, and networking events.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'AI portrait use cases',
				description:
					'Example of how Lift AI Portrait Generator creates professional AI portraits and headshots for various use cases.',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/ai-portrait-generator#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is Lift AI Portrait Generator?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift AI Portrait Generator is a premium online tool that instantly creates professional business headshots and AI portraits with studio-quality results — ideal for LinkedIn profiles, corporate websites, or personal branding.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift AI Portrait Generator work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Simply upload a few selfies to Lift's AI headshot generator tool. Our AI automatically generates professional portraits in your chosen styles and backgrounds in minutes — no physical photo shoot or editing experience needed. Download high-quality headshots ready for immediate use.",
					},
				},
				{
					'@type': 'Question',
					name: 'Will it keep hair, edges, and fine details?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Lift AI Portrait Generator uses advanced edge detection to preserve delicate elements like hair, fur, shadows, and soft textures — so you get clean, natural-looking cutouts without manual touch-ups.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All images generated or edited in Lift can be exported without watermark when you're on the Pro plan. Download them in high-res PNG or JPG — ready for social media, print, ads, or e-commerce.",
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-portrait-generator#why-lift',
			name: "Why Lift's AI Portrait Generator stands out",
			description:
				"Lift's AI Portrait Generator combines professional-grade AI, ease of use, and affordability — making it a trusted choice for millions of users worldwide.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Photo to portrait online',
				description:
					"Comparison of Lift's AI Portrait Generator benefits — high-quality results, intelligent rendering, multi-platform access, and cost savings.",
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-portrait-generator#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/ai-portrait-generator#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'AI Portrait Generator',
					item: 'https://liftapp.ai/ai-portrait-generator',
				},
			],
		},
	],
};
